import React, { useEffect, useRef, useState } from "react";
import {
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaClipboardCheck,
  FaCog,
  FaGasPump,
  FaHardHat,
  FaHospital,
  FaHotel,
  FaIndustry,
  FaMapMarkerAlt,
  FaPassport,
  FaPlayCircle,
  FaTools,
  FaTruck,
  FaUserCheck,
  FaUsers,
  FaWarehouse
} from "react-icons/fa";
import "./CompactHomepage.css";
import officeVideo from "../assets/videos/officevid.mp4";

const stats = [
  { icon: <FaBriefcase />, value: 25, suffix: "+", label: "Years of Experience" },
  { icon: <FaUsers />, value: 50000, suffix: "+", label: "Candidates Placed" },
  { icon: <FaBuilding />, value: 300, suffix: "+", label: "Satisfied Clients" },
  { icon: <FaMapMarkerAlt />, value: 10, suffix: "+", label: "Countries Served" }
];

const whyCards = [
  {
    icon: <FaClipboardCheck />,
    title: "Licensed & Government Registered",
    text: "Approved by Ministry of Interior & MOFA, Government of Pakistan."
  },
  {
    icon: <FaBriefcase />,
    title: "25+ Years of Excellence",
    text: "Over two decades of experience in international recruitment services."
  },
  {
    icon: <FaUsers />,
    title: "Wide Pool of Skilled Workers",
    text: "Access to a large database of pre-screened and skilled candidates."
  },
  {
    icon: <FaPassport />,
    title: "Fast & Reliable Visa Processing",
    text: "Efficient documentation and visa processing for quick deployment."
  },
  {
    icon: <FaCheckCircle />,
    title: "Trusted by Leading Companies",
    text: "Serving top organisations across Saudi Arabia and the Middle East."
  },
  {
    icon: <FaUserCheck />,
    title: "Dedicated Support Team",
    text: "Our team is always ready to assist you with the best possible support."
  }
];

const industries = [
  { icon: <FaHardHat />, title: "Construction", text: "Project manpower for site and civil works." },
  { icon: <FaCog />, title: "Engineering", text: "Technical workers for specialist roles." },
  { icon: <FaGasPump />, title: "Oil & Gas", text: "Industrial support for energy sectors." },
  { icon: <FaHospital />, title: "Healthcare", text: "Healthcare and support staffing." },
  { icon: <FaIndustry />, title: "Manufacturing", text: "Factory, plant and production teams." },
  { icon: <FaHotel />, title: "Hospitality", text: "Hotel, service and facilities staff." },
  { icon: <FaWarehouse />, title: "Ceramic Industry", text: "Factory and production manpower support." },
  { icon: <FaTruck />, title: "Transport", text: "Drivers, logistics and transport support roles." },
  { icon: <FaTools />, title: "Facility Management", text: "Maintenance and facilities workforce support." },
  { icon: <FaUsers />, title: "And Many More", text: "Flexible manpower support for varied employer needs." }
];

const process = [
  { icon: <FaClipboardCheck />, label: "Requirement Received" },
  { icon: <FaUsers />, label: "Candidate Screening" },
  { icon: <FaUserCheck />, label: "Interviews & Trade Test" },
  { icon: <FaCheckCircle />, label: "Medical Examination" },
  { icon: <FaPassport />, label: "Visa Processing" },
  { icon: <FaBriefcase />, label: "Departure & Deployment" }
];

const trustedCompanies = [
  { name: "Riyadh Ceramics Factory", logo: "/images/riyadhlogo.png" },
  { name: "Elkhereiji", logo: "/images/elkhereiji_logo.png" },
  { name: "Nesma International Water & Energy Technology Co. Ltd.", logo: "/images/nesma_logo.png" },
  { name: "MOVENPICK", logo: "/images/movenpicklogo.png" },
  { name: "Al Nahdi", logo: "/images/nahdilogo.png" },
  { name: "Al Ziady", logo: "/images/ziadylogo.png" },
  { name: "AliShar", logo: "/images/alisharlogo.png" }
];

const CompactHomepage = ({ t = {} }) => {
  const statsRef = useRef(null);
  const [counts, setCounts] = useState(stats.map(() => 0));
  const translatedStats = t.stats || stats.map((stat) => stat.label);
  const translatedWhyCards = t.whyCards || whyCards.map((card) => [card.title, card.text]);
  const translatedIndustries = t.industries || industries.map((industry) => [industry.title, industry.text]);
  const translatedProcess = t.process || process.map((stage) => stage.label);

  useEffect(() => {
    const target = statsRef.current;

    if (!target) {
      return undefined;
    }

    const runCounter = () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setCounts(stats.map((stat) => stat.value));
        return;
      }

      const start = performance.now();
      const duration = 3200;
      const stagger = 180;

      const tick = (time) => {
        const nextCounts = stats.map((stat, index) => {
          const itemProgress = Math.min(Math.max((time - start - index * stagger) / duration, 0), 1);
          const eased = 1 - Math.pow(1 - itemProgress, 2);
          return Math.round(stat.value * eased);
        });

        setCounts(nextCounts);

        if (nextCounts.some((count, index) => count < stats[index].value)) {
          requestAnimationFrame(tick);
        }
      };

      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runCounter();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section className="stats-strip" ref={statsRef} aria-label="Pak Arabian manpower statistics">
        <div className="stats-strip-inner">
          {stats.map((stat, index) => (
            <article className="stat-item" key={stat.label}>
              <div className="stat-icon">{stat.icon}</div>
              <div>
                <strong>{counts[index].toLocaleString()}{stat.suffix}</strong>
                <span>{translatedStats[index] || stat.label}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="why-reference-section">
        <div className="compact-section-heading">
          <h2>
            <span>{t.whyTitle?.[0] || "Why Choose"}</span>
            <span>{t.whyTitle?.[1] || "Pak Arabian"} <em>{t.whyTitle?.[2] || "Manpower Services?"}</em></span>
          </h2>
        </div>
        <div className="why-reference-grid">
          {whyCards.map((card, index) => (
            <article className="reference-card why-reference-card" key={card.title}>
              <div className="reference-card-icon">{card.icon}</div>
              <h3>{translatedWhyCards[index]?.[0] || card.title}</h3>
              <p>{translatedWhyCards[index]?.[1] || card.text}</p>
            </article>
          ))}
        </div>
      </section>
      
      <section className="video-about-section" id="about">
        <div className="video-about-inner">
          <div className="video-about-media">
            <video className="video-about-player" controls autoPlay loop muted playsInline>
              <source src={officeVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="video-about-badge">
              <FaPlayCircle />
              <span>{t.aboutPoints?.[0] || "Pak Arabian Manpower Services"}</span>
            </div>
          </div>
          <div className="video-about-copy">
            <p className="section-kicker">{t.aboutKicker || "About Pak Arabian"}</p>
            <h2 className="split-section-title">
              <span>{t.aboutTitle?.[0] || "A Professional Manpower Partner"}</span>
              <em>{t.aboutTitle?.[1] || "For Saudi Arabia & The Middle East"}</em>
            </h2>
            <p>
              {t.aboutText || "We support international employers with practical recruitment coordination, candidate screening, documentation follow-up, and deployment readiness from Pakistan."}
            </p>
            <div className="video-about-points">
              {(t.aboutPoints || ["Licensed manpower support", "Employer-focused coordination", "Reliable overseas deployment process"]).map((point) => (
                <span key={point}>{point}</span>
              ))}
            </div>
            <div className="video-about-actions">
              <a href="/Company-Profile.pdf" download>{t.downloadProfile || "Download Company Profile"}</a>
              <a href="/Company-Profile.pdf" target="_blank" rel="noopener noreferrer">{t.viewProfile || "View Company Profile"}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="trusted-reference-section" aria-label="Trusted by companies">
        <div className="trusted-reference-heading">
          <h2 className="split-section-title">
            <span>{t.trustedTitle?.[0] || "Trusted"}</span>
            <em>{t.trustedTitle?.[1] || "By Leading Companies"}</em>
          </h2>
          <span>{t.trustedText || "Recognised by companies across multiple industries."}</span>
        </div>
        <div className="trusted-reference-carousel">
          <div className="trusted-reference-track">
            {[trustedCompanies, trustedCompanies].map((group, groupIndex) => (
              <div className="trusted-reference-group" aria-hidden={groupIndex === 1} key={groupIndex}>
                {group.map((company) => (
                  <div className="trusted-reference-logo" key={`${groupIndex}-${company.name}`}>
                    <img src={company.logo} alt={company.name} loading="lazy" />
                    <span>{company.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="industries-reference-section" id="services">
        <div className="reference-heading-row">
          <h2 className="split-section-title">
            <span>{t.industriesTitle?.[0] || "Industries"}</span>
            <em>{t.industriesTitle?.[1] || "We Serve"}</em>
          </h2>
        </div>
        <div className="industries-reference-grid">
          {industries.map((industry, index) => (
            <article className="reference-card industry-reference-card" key={industry.title}>
              <div className="reference-card-icon">{industry.icon}</div>
              <h3>{translatedIndustries[index]?.[0] || industry.title}</h3>
              <p>{translatedIndustries[index]?.[1] || industry.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="process-reference-section" aria-label="Our Recruitment Process">
        <div className="process-reference-inner">
          <h2>{t.processTitle?.[0] || "Our Recruitment"} <em>{t.processTitle?.[1] || "Process"}</em></h2>
          <div className="process-reference-track">
            {process.map((stage, index) => (
              <article className="process-reference-stage" key={stage.label}>
                <div className="process-reference-icon">{stage.icon}</div>
                <strong>{String(index + 1).padStart(2, "0")}</strong>
                <span>{translatedProcess[index] || stage.label}</span>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default CompactHomepage;
